// Stage 50: Gadfly turn live-store + hook.
//
// A tiny pubsub holding the CURRENT TURN's Gadfly state (bites + persona
// swat tags) for the operator inspector pane to render. The store is fed
// by useChat as it decodes `gadfly_bites` and `gadfly_swat` SSE events.
//
// Hard-line memorylessness (TINS §Implementation Notes #8) is honoured: a
// new bites event REPLACES the store contents — prior turn's bites vanish.
// There is no history, no accumulation, no persistence. When the operator
// closes and re-opens SettingsModal mid-conversation, the inspector shows
// whatever turn is current at that moment; on refresh, it shows nothing.

import { useSyncExternalStore } from 'react';
import type { Bite, SwatEntry } from '../api/gadfly';

export interface GadflyTurnState {
  turnId: string | null;
  bites: Bite[];
  swatEntries: SwatEntry[];
  failed: boolean;
  failureReason: string | null;
  generatedAt: string | null;
  durationMs: number | null;
  stripeRatioApplied: { keep: number; cut: number } | null;
  modelUsed: string | null;
  // Stage 97: flipped to true when useChat receives the SSE `done`
  // event for the current turnId. Lets the WARP visualization layer
  // distinguish "swat hasn't arrived yet" from "the turn closed without
  // a swat — this bite is now an implicit REJECT."
  turnEnded: boolean;
}

const EMPTY: GadflyTurnState = {
  turnId: null,
  bites: [],
  swatEntries: [],
  failed: false,
  failureReason: null,
  generatedAt: null,
  durationMs: null,
  stripeRatioApplied: null,
  modelUsed: null,
  turnEnded: false,
};

let snapshot: GadflyTurnState = EMPTY;
const listeners = new Set<() => void>();

function publish(next: GadflyTurnState): void {
  snapshot = next;
  for (const l of listeners) l();
}

/** Called by useChat when a `gadfly_bites` SSE frame arrives. */
export function applyGadflyBites(input: {
  turnId: string;
  bites: Bite[];
  failed: boolean;
  failureReason: string | null;
  generatedAt: string;
  durationMs: number;
  stripeRatioApplied: { keep: number; cut: number };
  modelUsed: string;
}): void {
  publish({
    turnId: input.turnId,
    bites: input.bites,
    swatEntries: [],
    failed: input.failed,
    failureReason: input.failureReason,
    generatedAt: input.generatedAt,
    durationMs: input.durationMs,
    stripeRatioApplied: input.stripeRatioApplied,
    modelUsed: input.modelUsed,
    // Stage 97: a fresh turn re-opens the closed/implicit-REJECT view.
    turnEnded: false,
  });
}

/**
 * Stage 97: called by useChat when the SSE `done` event arrives. Flips
 * the current snapshot to `turnEnded: true` so the WARP visualization
 * can render untagged bites as implicit REJECTs. turnId-guarded so a
 * stale `done` event for a prior turn (defensive — shouldn't happen)
 * is a no-op.
 */
export function applyGadflyTurnEnd(input: { turnId: string }): void {
  if (snapshot.turnId !== input.turnId) return;
  if (snapshot.turnEnded) return;
  publish({ ...snapshot, turnEnded: true });
}

/** Called by useChat when a `gadfly_swat` SSE frame arrives. */
export function applyGadflySwat(input: {
  turnId: string;
  entry: SwatEntry;
}): void {
  // Ignore swat events whose turnId doesn't match the current snapshot —
  // they're for a prior turn whose bites have already been displaced.
  if (snapshot.turnId !== input.turnId) return;
  const filtered = snapshot.swatEntries.filter(
    (e) => e.biteIndex !== input.entry.biteIndex,
  );
  publish({
    ...snapshot,
    swatEntries: [...filtered, input.entry],
  });
}

/** Test-only: reset to EMPTY. */
export function _resetGadflyTurnForTest(): void {
  publish(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): GadflyTurnState {
  return snapshot;
}

/** React hook — returns the current turn's Gadfly state, re-renders on change. */
export function useGadflyTurn(): GadflyTurnState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
